﻿(function () {
  const DEFAULT_BREAKPOINTS = [
    { min: 0, items: 1 },
    { min: 480, items: 2 },
    { min: 768, items: 3 },
    { min: 1024, items: 4 },
    { min: 1100, items: 5 },
  ];

  const instances = new Set();

  function normalizeBreakpoints(input) {
    if (!input) {
      return DEFAULT_BREAKPOINTS;
    }

    if (Array.isArray(input)) {
      return input
        .map((entry) => ({ min: Number(entry.min) || 0, items: Math.max(1, Number(entry.items) || 1) }))
        .sort((left, right) => left.min - right.min);
    }

    if (typeof input === 'object') {
      return Object.entries(input)
        .map(([min, items]) => ({ min: Number(min) || 0, items: Math.max(1, Number(items) || 1) }))
        .sort((left, right) => left.min - right.min);
    }

    return DEFAULT_BREAKPOINTS;
  }

  function getGap(root) {
    const computed = window.getComputedStyle(root);
    const gap = computed.getPropertyValue('--carousel-gap').trim();
    const parsed = Number.parseFloat(gap);
    return Number.isFinite(parsed) ? parsed : 20;
  }

  function getState(root) {
    if (!root.__simpleCarouselState) {
      root.__simpleCarouselState = {};
    }

    return root.__simpleCarouselState;
  }

  function getMaxItemsForWidth(root, gap, minCardWidth) {
    const safeMinCardWidth = Math.max(160, Number(minCardWidth) || 250);
    const width = Math.max(0, root.clientWidth);
    const maxItems = Math.floor((width + gap) / (safeMinCardWidth + gap));
    return Math.max(1, maxItems || 1);
  }

  function getItemsPerView(breakpoints) {
    const viewportWidth = window.innerWidth;
    let items = 1;

    breakpoints.forEach((entry) => {
      if (viewportWidth >= entry.min) {
        items = entry.items;
      }
    });

    return Math.max(1, items);
  }

  function markSlides(root) {
    Array.from(root.children).forEach((child) => {
      if (child.classList && (child.classList.contains('owl-nav') || child.classList.contains('simple-carousel-nav'))) {
        return;
      }

      child.classList.add('owl-item');
    });
  }

  function scrollByPage(root, direction) {
    const state = getState(root);
    if (!root) {
      return;
    }

    const maxScrollLeft = Math.max(0, root.scrollWidth - root.clientWidth);
    const currentScrollLeft = root.scrollLeft;
    const firstSlide = root.querySelector('.owl-item');
    const step = firstSlide
      ? firstSlide.getBoundingClientRect().width + getGap(root)
      : root.clientWidth;

    const target = currentScrollLeft + step * direction;

    if (state.loop !== false) {
      const edgeTolerance = 2;

      if (target > maxScrollLeft) {
        // Primero llega al final real; solo luego hace wrap al inicio.
        if (currentScrollLeft < maxScrollLeft - edgeTolerance) {
          root.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
          return;
        }

        root.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      if (target < 0) {
        // Primero llega al inicio real; solo luego hace wrap al final.
        if (currentScrollLeft > edgeTolerance) {
          root.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }

        root.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
        return;
      }

      root.scrollTo({ left: target, behavior: 'smooth' });
      return;
    }

    const clampedTarget = Math.max(0, Math.min(maxScrollLeft, target));
    root.scrollTo({ left: clampedTarget, behavior: 'smooth' });
  }

  function buildShell(root) {
    const state = getState(root);
    if (state.nav) {
      return;
    }

    const nav = document.createElement('div');
    nav.className = 'owl-nav simple-carousel-nav';
    nav.setAttribute('aria-label', 'Carousel controls');

    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'owl-prev';
    prevButton.setAttribute('aria-label', 'Previous');
    prevButton.innerHTML = '&#10094;';

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'owl-next';
    nextButton.setAttribute('aria-label', 'Next');
    nextButton.innerHTML = '&#10095;';

    nav.appendChild(prevButton);
    nav.appendChild(nextButton);

    const host = root.parentElement || root;
    if (host !== root) {
      host.classList.add('simple-carousel-host');
    }
    host.appendChild(nav);

    state.nav = nav;
    state.prevButton = prevButton;
    state.nextButton = nextButton;

    prevButton.addEventListener('click', () => scrollByPage(root, -1));
    nextButton.addEventListener('click', () => scrollByPage(root, 1));
  }

  function updateLayout(root) {
    const state = getState(root);
    if (!root) {
      return;
    }

    const breakpoints = normalizeBreakpoints(state.breakpoints);
    state.breakpoints = breakpoints;
    const baseItemsPerView = getItemsPerView(breakpoints);
    const gap = getGap(root);
    const maxItemsByWidth = getMaxItemsForWidth(root, gap, state.minCardWidth);
    const itemsPerView = Math.min(baseItemsPerView, maxItemsByWidth);
    const totalSlides = root.querySelectorAll('.owl-item').length;
    const canScroll = totalSlides > itemsPerView;

    state.itemsPerView = itemsPerView;
    root.style.setProperty('--carousel-items', String(itemsPerView));
    root.style.setProperty('--carousel-gap', '20px');

    if (state.nav) {
      state.nav.hidden = !canScroll;
      state.nav.setAttribute('aria-hidden', String(!canScroll));
    }

    const maxScrollLeft = Math.max(0, root.scrollWidth - root.clientWidth);
    if (root.scrollLeft > maxScrollLeft) {
      root.scrollLeft = maxScrollLeft;
    }
  }

  function mount(root, options = {}) {
    if (!root) {
      return null;
    }

    const state = getState(root);
    state.breakpoints = normalizeBreakpoints(options.breakpoints || state.breakpoints);
    state.loop = options.loop !== false;
    state.minCardWidth = Number(options.minCardWidth) || state.minCardWidth || 250;

    buildShell(root);
    markSlides(root);
    updateLayout(root);
    instances.add(root);
    return root;
  }

  function initAll() {
    document.querySelectorAll('.simple-carousel').forEach((root) => {
      mount(root);
    });
  }

  window.SimpleCarousel = {
    mount,
    initAll,
    refresh(root) {
      if (root) {
        updateLayout(root);
        return;
      }

      instances.forEach((instance) => updateLayout(instance));
    },
  };

  window.addEventListener('resize', () => {
    instances.forEach((instance) => updateLayout(instance));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
}());
